import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Wraps <span class="gloss-term"> elements (rendered from server-generated HTML)
 * with hover cards. All hoverable terms live in the single `glossary`
 * collection — only rows with a non-empty `hover_definition` are wrapped by
 * the server, so any span carrying `data-term-id` maps directly to a
 * glossary row here.
 *
 * The tooltip shows the term's short `hover_definition` blurb + a link to
 * the full definition on the Glossary page.
 *
 * Rendered into document.body via a portal so absolute positioning always works,
 * regardless of any transformed/positioned ancestor in the layout.
 */
export default function GlossaryEnhancer({ containerRef }) {
  const [terms, setTerms] = useState([]);
  const [active, setActive] = useState(null); // {item, x, y}
  const timer = useRef(null);

  useEffect(() => {
    api.get("/glossary").then(r => setTerms(r.data)).catch(() => setTerms([]));
  }, []);

  useEffect(() => {
    if (!containerRef?.current || terms.length === 0) return;
    const container = containerRef.current;
    const glossaryMap = Object.fromEntries(terms.map(t => [t.term_id, t]));

    const onEnter = (e) => {
      clearTimeout(timer.current);
      const el = e.currentTarget;
      const tid = el.dataset.termId;
      const item = tid ? glossaryMap[tid] : null;
      if (!item) return;
      const rect = el.getBoundingClientRect();
      // Position tooltip below the term relative to the viewport.
      // Clamp to viewport so it never falls off-screen on the right edge.
      const tooltipWidth = 320;
      const maxLeft = window.innerWidth - tooltipWidth - 12;
      setActive({
        item,
        x: Math.min(Math.max(8, rect.left), Math.max(8, maxLeft)),
        y: rect.bottom + 6,
      });
    };
    const onLeave = () => {
      timer.current = setTimeout(() => setActive(null), 200);
    };

    // Attach listeners to all .gloss-term elements currently in the container,
    // and re-run when the DOM changes (blog HTML is injected via dangerouslySetInnerHTML).
    const wire = () => {
      const els = container.querySelectorAll(".gloss-term");
      els.forEach(el => {
        // guard: don't double-bind
        if (el.__gh_bound) return;
        el.__gh_bound = true;
        el.addEventListener("mouseenter", onEnter);
        el.addEventListener("mouseleave", onLeave);
      });
      return els;
    };
    wire();

    const mo = new MutationObserver(() => wire());
    mo.observe(container, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      container.querySelectorAll(".gloss-term").forEach(el => {
        el.removeEventListener("mouseenter", onEnter);
        el.removeEventListener("mouseleave", onLeave);
        delete el.__gh_bound;
      });
    };
  }, [terms, containerRef]);

  if (!active) return null;

  const { item } = active;
  const blurb = (item.hover_definition || "").trim() || item.definition;

  return createPortal(
    <div
      data-testid="glossary-tooltip"
      onMouseEnter={() => clearTimeout(timer.current)}
      onMouseLeave={() => setActive(null)}
      style={{ position: "fixed", top: active.y, left: active.x, width: 320 }}
      className="z-[10000] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-xl p-4"
    >
      <div className="font-heading font-semibold text-slate-900 dark:text-slate-100">{item.term}</div>
      <div className="text-sm text-slate-600 dark:text-slate-300 mt-1 leading-relaxed line-clamp-4">
        {blurb}
      </div>
      <Link
        to={`/glossary?term_id=${item.term_id}`}
        className="mt-2 inline-block text-xs uppercase tracking-widest text-sky-700 dark:text-sky-300 hover:text-sky-900 dark:hover:text-sky-100"
      >
        Read full definition →
      </Link>
    </div>,
    document.body
  );
}
